import { layers } from "@/configs/map/layers";
import { useCurrentLanguage } from "@/stores/language";
import { useMapStore } from "@/stores/map";
import type { MapViewState } from "@deck.gl/core";
import { DeckGL } from "@deck.gl/react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { MapRef } from "react-map-gl";
import { Map } from "react-map-gl";
import { useShallow } from "zustand/react/shallow";
import "./StadiaMap.css";

const BASE_MAP_STYLE = "https://tiles-eu.stadiamaps.com/styles/osm_bright.json";

export function StadiaMap({
  id,
  workspaceId,
  interactive = true,
  center = [11, 56],
  zoom = 4,
  width = "100%",
  height = "100%",
  radius = 0,
  onHover,
  onClick,
  onViewStateChange,
}: {
  id: string;
  workspaceId?: string;
  interactive?: boolean;
  center?: [number, number];
  zoom?: number;
  width?: string | number;
  height?: string | number;
  radius?: string | number;
  onHover?: (info: any) => void;
  onClick?: (info: any) => void;
  onViewStateChange?: (viewState: MapViewState) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const deckRef = useRef<any>(null);
  const deckInstanceStoredRef = useRef(false);
  const previousLanguageRef = useRef<string | null>(null);
  const currentLanguage = useCurrentLanguage();
  const effectiveWorkspaceId = workspaceId || "default";

  const mapActions = useMapStore(
    useShallow((state) => ({
      setActiveWorkspace: state.setActiveWorkspace,
      setMapInstance: state.setMapInstance,
      setMapLoaded: state.setMapLoaded,
      getViewState: state.getViewState,
      setViewState: state.setViewState,
      getLayersForWorkspace: state.getLayersForWorkspace,
      setStaticData: state.setStaticData,
      setLoadingStaticData: state.setLoadingStaticData,
    }))
  );

  const {
    setActiveWorkspace,
    setMapInstance,
    setMapLoaded,
    getViewState,
    setViewState,
    getLayersForWorkspace,
    setStaticData,
    setLoadingStaticData,
  } = mapActions;

  const layerState = useMapStore(
    useShallow((state) => {
      const workspaceGroups = state.groups[effectiveWorkspaceId];
      const unifiedGroups = state.unifiedGroups[effectiveWorkspaceId];

      // Build unified layer visibility key for reactive updates
      const unifiedVisibilityKey = Object.entries(state.layerVisibility)
        .filter(([_, isVisible]) => isVisible)
        .map(([layerId]) => layerId)
        .sort()
        .join(",");

      return {
        staticDataKeys: Object.keys(state.staticData).sort().join(","),
        staticLayersKey: Object.entries(state.staticLayers)
          .filter(([_, isVisible]) => isVisible)
          .map(([layerId]) => layerId)
          .sort()
          .join(","),
        isLoadingStaticData: state.isLoadingStaticData,
        geoJSONLayerKeys: Object.keys(state.geoJSONLayers).sort().join(","),
        geoJSONLayerVisibility: Object.entries(state.geoJSONLayers)
          .map(
            ([layerId, layerData]) =>
              `${layerId}:${layerData?.isVisible ? "1" : "0"}`
          )
          .sort()
          .join(","),
        temporaryLayerCount: (state.temporaryLayers[effectiveWorkspaceId] || [])
          .length,
        // Use unified visibility for groups/chunks
        unifiedVisibilityKey,
        visibleGroupsKey: (workspaceGroups?.visibleGroups || [])
          .sort()
          .join(","),
        groupColumnName: workspaceGroups?.groupColumnName || null,
        // Track unified groups existence to ensure map reacts when they're created
        unifiedGroupsKey: unifiedGroups?.groups
          ? unifiedGroups.groups
              .map((g) => `${g.id}:${g.type}`)
              .sort()
              .join(",")
          : "",
        // Track when unified groups are created/updated to force map re-render
        unifiedGroupsUpdateKey: unifiedGroups?.groups
          ? `${unifiedGroups.groupColumnName || ""}-${
              unifiedGroups.groups.length
            }-${unifiedGroups.groups
              .map((g) => g.id)
              .sort()
              .join(",")
              .substring(0, 50)}`
          : "",
        // Build configs from unified state to ensure exact match with sidebar
        groupConfigsKey: unifiedGroups?.groupConfigs
          ? Object.keys(unifiedGroups.groupConfigs)
              .sort()
              .map(
                (k) =>
                  `${k}:${unifiedGroups.groupConfigs[k]?.color || ""}:${
                    unifiedGroups.groupConfigs[k]?.shape || ""
                  }`
              )
              .join(",")
          : Object.keys(workspaceGroups?.groupConfigs || {})
              .sort()
              .map(
                (k) =>
                  `${k}:${workspaceGroups?.groupConfigs[k]?.color || ""}:${
                    workspaceGroups?.groupConfigs[k]?.shape || ""
                  }`
              )
              .join(","),
        pointConfigsKey: Object.keys(workspaceGroups?.pointConfigs || {})
          .sort()
          .map(
            (k) =>
              `${k}:${workspaceGroups?.pointConfigs?.[k]?.color || ""}:${
                workspaceGroups?.pointConfigs?.[k]?.shape || ""
              }:${workspaceGroups?.pointConfigs?.[k]?.isVisible ? "1" : "0"}`
          )
          .join(","),
        chunkConfigsKey: unifiedGroups?.groupConfigs
          ? Object.keys(unifiedGroups.groupConfigs)
              .filter((k) => {
                const group = unifiedGroups.groups.find(
                  (g) => g.id === k && g.type === "chunk"
                );
                return !!group;
              })
              .sort()
              .map((k) => {
                const config = unifiedGroups.groupConfigs[k];
                const group = unifiedGroups.groups.find(
                  (g) => g.id === k && g.type === "chunk"
                );
                return `${k}:${config?.color || group?.color || ""}:${
                  config?.shape || group?.shape || ""
                }:${config?.name || group?.displayName || ""}:${
                  group?.from || ""
                }:${group?.to || ""}`;
              })
              .join(",")
          : Object.keys(workspaceGroups?.chunkConfigs || {})
              .sort()
              .map(
                (k) =>
                  `${k}:${workspaceGroups?.chunkConfigs?.[k]?.color || ""}:${
                    workspaceGroups?.chunkConfigs?.[k]?.shape || ""
                  }:${workspaceGroups?.chunkConfigs?.[k]?.displayName || ""}:${
                    workspaceGroups?.chunkConfigs?.[k]?.from || ""
                  }:${workspaceGroups?.chunkConfigs?.[k]?.to || ""}`
              )
              .join(","),
        layerRegionConfigsKey: Object.keys(
          workspaceGroups?.layerRegionConfigs || {}
        )
          .sort()
          .map(
            (k) =>
              `${k}:${workspaceGroups?.layerRegionConfigs?.[k]?.color || ""}:${
                workspaceGroups?.layerRegionConfigs?.[k]?.displayName || ""
              }:${
                workspaceGroups?.layerRegionConfigs?.[k]?.isVisible ? "1" : "0"
              }`
          )
          .join(","),
        visualizationMode: workspaceGroups?.visualizationMode || "points",
        visualizationLayerType: workspaceGroups?.visualizationLayerType || null,
        layerConfigKey: workspaceGroups?.layerConfig
          ? `${workspaceGroups.layerConfig.color || ""}:${
              workspaceGroups.layerConfig.shape || ""
            }`
          : "",
        chunksKey: workspaceGroups?.chunks
          ? workspaceGroups.chunks
              .map(
                (c) =>
                  `${c.value}:${c.label}:${c.color || ""}:${c.values.join(",")}`
              )
              .sort()
              .join("|")
          : "",
        categoryColorMapKey: workspaceGroups?.categoryColorMap
          ? Object.keys(workspaceGroups.categoryColorMap)
              .sort()
              .map((k) => `${k}:${workspaceGroups.categoryColorMap?.[k] || ""}`)
              .join("|")
          : "",
        textLayersVisible: state.textLayersVisible,
        roadsLayersVisible: state.roadsLayersVisible,
      };
    })
  );

  const geoJSONLayerDataHash = useMemo(() => {
    const layers = useMapStore.getState().geoJSONLayers;
    const hashes = Object.entries(layers).map(([layerId, layerData]) => {
      if (!layerData?.data?.features) return `${layerId}:0`;
      const featureCount = layerData.data.features.length;
      const sampleSize = Math.min(10, featureCount);
      const categories = new Set<string>();
      const colors = new Set<string>();
      for (let i = 0; i < sampleSize; i++) {
        const feature = layerData.data.features[i];
        if (feature?.properties?._category) {
          categories.add(feature.properties._category);
        }
        if (feature?.properties?._color) {
          colors.add(feature.properties._color);
        }
      }
      const categoryStr = Array.from(categories).sort().join(",");
      const colorStr = Array.from(colors).sort().join(",");
      return `${layerId}:${featureCount}:${categoryStr}:${colorStr}`;
    });
    return hashes.sort().join("|");
  }, [
    layerState.geoJSONLayerKeys,
    layerState.geoJSONLayerVisibility,
    layerState.groupColumnName,
    layerState.visibleGroupsKey,
    layerState.chunksKey,
    layerState.chunkConfigsKey,
    layerState.layerRegionConfigsKey,
    layerState.visualizationMode,
    layerState.visualizationLayerType,
  ]);

  const storedViewState = getViewState(effectiveWorkspaceId);
  const initialViewState: MapViewState = useMemo(
    () =>
      storedViewState || {
        longitude: center[0],
        latitude: center[1],
        zoom: zoom,
        pitch: 0,
        bearing: 0,
      },
    [storedViewState, center, zoom]
  );

  const mapStyle = useMemo(() => {
    const styleUrl = new URL(BASE_MAP_STYLE);
    styleUrl.searchParams.set("lang", currentLanguage);
    return styleUrl.toString();
  }, [currentLanguage]);

  useEffect(() => {
    if (layerState.staticDataKeys.length > 0) return;

    const loadStaticData = async () => {
      setLoadingStaticData(true);

      try {
        const dataPromises = Object.entries(layers).map(
          async ([layerId, layerConfig]) => {
            const response = await fetch(layerConfig.source.data);
            if (!response.ok) {
              throw new Error(
                `Failed to load ${layerId} data: ${response.statusText}`
              );
            }
            const data = await response.json();
            return [layerId, data] as const;
          }
        );

        const loadedData = await Promise.all(dataPromises);
        const newStaticData = Object.fromEntries(loadedData);

        setStaticData(newStaticData);
        setLoadingStaticData(false);
      } catch (error) {
        setLoadingStaticData(false);
      }
    };

    loadStaticData();
  }, [
    layerState.staticDataKeys,
    setStaticData,
    setLoadingStaticData,
    layerState.isLoadingStaticData,
  ]);

  const layersUpdateKey = useMemo(() => {
    return `${layerState.groupColumnName || "null"}-${
      layerState.visibleGroupsKey
    }-${layerState.chunksKey}`;
  }, [
    layerState.groupColumnName,
    layerState.visibleGroupsKey,
    layerState.chunksKey,
  ]);

  const dataLabelsVisible = useMapStore((state) => state.dataLabelsVisible);

  const deckLayers = useMemo(() => {
    const layers = getLayersForWorkspace(effectiveWorkspaceId);
    return [...layers];
  }, [
    effectiveWorkspaceId,
    layersUpdateKey,
    layerState.unifiedVisibilityKey, // Subscribe to unified visibility changes
    layerState.groupColumnName,
    layerState.visibleGroupsKey,
    layerState.unifiedGroupsKey, // Subscribe to unified groups creation/changes
    layerState.unifiedGroupsUpdateKey, // Force re-render when unified groups are created/updated
    layerState.groupConfigsKey,
    layerState.chunkConfigsKey,
    layerState.layerRegionConfigsKey,
    layerState.visualizationMode,
    layerState.visualizationLayerType,
    layerState.layerConfigKey,
    layerState.chunksKey,
    layerState.pointConfigsKey,
    layerState.categoryColorMapKey,
    geoJSONLayerDataHash,
    layerState.geoJSONLayerKeys,
    layerState.geoJSONLayerVisibility,
    layerState.staticDataKeys,
    layerState.staticLayersKey,
    layerState.temporaryLayerCount,
    dataLabelsVisible, // Subscribe to data labels visibility changes
  ]);

  const debouncedSetViewStateRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const handleViewStateChange = useCallback(
    (params: any) => {
      if (params.viewState) {
        const clampedViewState: MapViewState = {
          ...params.viewState,
          pitch: 0,
          bearing: 0,
        };

        if (onViewStateChange) {
          onViewStateChange(clampedViewState);
        }

        if (debouncedSetViewStateRef.current) {
          clearTimeout(debouncedSetViewStateRef.current);
        }

        debouncedSetViewStateRef.current = setTimeout(() => {
          setViewState(effectiveWorkspaceId, clampedViewState);
          debouncedSetViewStateRef.current = null;
        }, 100);
      }
    },
    [effectiveWorkspaceId, setViewState, onViewStateChange]
  );

  useEffect(() => {
    return () => {
      if (debouncedSetViewStateRef.current) {
        clearTimeout(debouncedSetViewStateRef.current);
      }
    };
  }, []);

  const mapRef = useRef<MapRef | null>(null);

  const updateNativeLayerVisibility = useCallback(() => {
    const mapRefInstance = mapRef.current;
    if (!mapRefInstance) return;

    // Ensure map is loaded before accessing it
    if (!mapRefInstance.isStyleLoaded()) return;

    // Get the underlying mapbox/maplibre map instance
    // MapRef.getMap() returns the underlying map instance
    let map: any;
    if (typeof (mapRefInstance as any).getMap === "function") {
      map = (mapRefInstance as any).getMap();
    } else {
      // Fallback: MapRef might be the map instance directly in some versions
      map = mapRefInstance as any;
    }

    if (!map || typeof map.setLayoutProperty !== "function") {
      // Map instance not available or doesn't have required methods
      return;
    }

    const placeLabelLayerIds = [
      "place-labels",
      "place-city",
      "place-town",
      "place-village",
      "place-hamlet",
      "place-suburb",
      "place-neighbourhood",
      "place-state",
      "place-country",
      "place-continent",
    ];

    const waterLabelLayerIds = [
      "water-name",
      "water-labels",
      "marine-label",
      "waterway-label",
      "ocean-label",
      "sea-label",
    ];

    const roadLayerIds = [
      "road",
      "roads",
      "highway",
      "highways",
      "street",
      "streets",
      "bridge",
      "bridges",
      "tunnel",
      "tunnels",
      "road-label",
      "highway-label",
      "street-label",
    ];

    const textLayerIds = [...placeLabelLayerIds, ...waterLabelLayerIds];

    // Update text layers visibility
    textLayerIds.forEach((layerId) => {
      try {
        if (map.getLayer(layerId)) {
          map.setLayoutProperty(
            layerId,
            "visibility",
            layerState.textLayersVisible ? "visible" : "none"
          );
        }
      } catch (error) {
        // Layer might not exist in this style, ignore
      }
    });

    // Update road layers visibility
    roadLayerIds.forEach((layerId) => {
      try {
        if (map.getLayer(layerId)) {
          map.setLayoutProperty(
            layerId,
            "visibility",
            layerState.roadsLayersVisible ? "visible" : "none"
          );
        }
      } catch (error) {
        // Layer might not exist in this style, ignore
      }
    });

    // Update layers by pattern matching
    const style = map.getStyle();
    if (style && style.layers) {
      style.layers.forEach((layer: any) => {
        if (layer.id) {
          const layerId = layer.id.toLowerCase();
          const isTextLayer =
            layer.type === "symbol" &&
            (layerId.includes("place") ||
              layerId.includes("water") ||
              layerId.includes("marine") ||
              layerId.includes("ocean") ||
              layerId.includes("sea"));
          const isRoadLayer =
            layerId.includes("road") ||
            layerId.includes("highway") ||
            layerId.includes("street") ||
            layerId.includes("bridge") ||
            layerId.includes("tunnel");

          if (isTextLayer) {
            try {
              map.setLayoutProperty(
                layer.id,
                "visibility",
                layerState.textLayersVisible ? "visible" : "none"
              );
            } catch (error) {
              // Layer might not exist, ignore
            }
          } else if (isRoadLayer) {
            try {
              map.setLayoutProperty(
                layer.id,
                "visibility",
                layerState.roadsLayersVisible ? "visible" : "none"
              );
            } catch (error) {
              // Layer might not exist, ignore
            }
          }
        }
      });
    }
  }, [layerState.textLayersVisible, layerState.roadsLayersVisible]);

  const handleMapLoad = useCallback(
    (event: any) => {
      const mapInstance = event.target;
      if (mapInstance) {
        mapRef.current = mapInstance;

        const initializeMapElements = () => {
          updateNativeLayerVisibility();
        };

        if (mapInstance.isStyleLoaded()) {
          initializeMapElements();
        } else {
          mapInstance.once("styledata", initializeMapElements);
        }
      }
    },
    [updateNativeLayerVisibility]
  );

  // Update native layer visibility when state changes
  useEffect(() => {
    const mapRefInstance = mapRef.current;
    if (!mapRefInstance) return;

    if (mapRefInstance.isStyleLoaded()) {
      updateNativeLayerVisibility();
    } else {
      mapRefInstance.once("styledata", updateNativeLayerVisibility);
    }
  }, [updateNativeLayerVisibility]);

  const handleDeckLoad = useCallback(() => {
    if (deckRef.current && !deckInstanceStoredRef.current) {
      const currentViewState =
        getViewState(effectiveWorkspaceId) || initialViewState;
      setMapInstance(effectiveWorkspaceId, deckRef.current, currentViewState);
      setMapLoaded(effectiveWorkspaceId, true);
      deckInstanceStoredRef.current = true;
    }
  }, [
    effectiveWorkspaceId,
    getViewState,
    initialViewState,
    setMapInstance,
    setMapLoaded,
  ]);

  useEffect(() => {
    if (workspaceId) {
      setActiveWorkspace(workspaceId);
    }
  }, [workspaceId, setActiveWorkspace]);

  useEffect(() => {
    if (previousLanguageRef.current !== currentLanguage) {
      previousLanguageRef.current = currentLanguage;
    }
  }, [currentLanguage, mapStyle]);

  const controller = useMemo(() => {
    if (!interactive) {
      return false;
    }
    return {
      dragRotate: false,
      touchRotate: false,
      dragPan: true,
      scrollZoom: true,
      doubleClickZoom: true,
      keyboard: true,
      touchZoom: true,
    };
  }, [interactive]);

  function getCursor({
    isDragging,
    isHovering,
  }: {
    isDragging: boolean;
    isHovering: boolean;
  }) {
    return isDragging ? "grabbing" : isHovering ? "pointer" : "default";
  }

  return (
    <div
      id={id}
      ref={containerRef}
      style={{
        width,
        height,
        borderRadius: radius,
        position: "relative",
      }}
      className="map"
    >
      <DeckGL
        ref={deckRef}
        useDevicePixels={1}
        initialViewState={initialViewState}
        onViewStateChange={handleViewStateChange}
        controller={controller}
        layers={deckLayers}
        onLoad={handleDeckLoad}
        onHover={onHover}
        onClick={onClick}
        parameters={{
          depthWriteEnabled: false,
        }}
        getCursor={getCursor}
        _typedArrayManagerProps={{
          overAlloc: 1.2,
          poolSize: 0,
        }}
        _framebuffer={null}
        _animate={false}
        style={{
          width: "100%",
          height: "100%",
        }}
      >
        <Map
          ref={mapRef}
          mapStyle={mapStyle}
          reuseMaps
          interactive={false}
          dragPan={false}
          dragRotate={false}
          scrollZoom={false}
          doubleClickZoom={false}
          keyboard={false}
          boxZoom={false}
          touchPitch={false}
          onLoad={handleMapLoad}
        />
      </DeckGL>
    </div>
  );
}
